import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  configureClient,
  indexedDbStore,
  replayQueue,
  type OfflineStore,
  type QueuedWrite,
  type RequestSpec,
} from '@whinerp/client';

/**
 * The queue, as the application sees it.
 *
 * One provider owns the store, so there is one answer to "what is waiting"
 * however many screens ask. Two copies of this state would show two different
 * counts in two places on the same screen, and the person would believe the
 * smaller one.
 *
 * Replay is driven by three things and nothing else: coming back online, the
 * application starting, and a person pressing Try now. No polling timer -
 * a queue that retries every thirty seconds while a server is refusing is a
 * queue that hides the refusal, and the browser already knows when the network
 * came back.
 */

export interface QueueState {
  writes: QueuedWrite[];
  online: boolean;
  syncing: boolean;
  /** Waiting for a network. Nothing here needs a decision yet. */
  waiting: number;
  /** Answered by the server with something only a person can settle. */
  needsYou: number;
  refused: number;
  replay: () => Promise<void>;
  refresh: () => Promise<void>;
  discard: (id: string) => Promise<void>;
  /** Null where the browser offers no storage; see the provider. */
  store: OfflineStore | null;
}

const QueueContext = createContext<QueueState | null>(null);

/**
 * Names a queued write in the words of whoever made it.
 *
 * Registered by a screen for as long as it is open, so the queue can say
 * "Priya Raman" rather than a path and a uuid. A person reading this list in a
 * car park three hours later has to recognise their own work in it.
 */
const labels = new Map<string, string>();

export function nameWritesOn(recordId: string, label: string): () => void {
  labels.set(recordId, label);
  return () => void labels.delete(recordId);
}

function describe(spec: RequestSpec): string {
  for (const value of Object.values(spec.pathParams ?? {})) {
    const named = labels.get(String(value));
    if (named) return named;
  }
  return spec.operationId;
}

export function OfflineQueueProvider({
  children,
  store,
}: {
  children: ReactNode;
  /** Injected in tests; the application builds its own from IndexedDB. */
  store?: OfflineStore;
}): JSX.Element {
  /*
   * No storage, no promise.
   *
   * Private browsing, storage switched off, an embedded webview - all of them
   * reach here with no IndexedDB, and the honest response is to have no queue
   * rather than a queue that throws on the first save. `configured` then reads
   * false and the indicator draws nothing, because a status light for a
   * feature that is not installed is worse than no light.
   */
  const [ready] = useState<OfflineStore | null>(() =>
    store ?? (typeof indexedDB === 'undefined' ? null : indexedDbStore()),
  );
  const [writes, setWrites] = useState<QueuedWrite[]>([]);
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [syncing, setSyncing] = useState(false);
  // One replay at a time. Two overlapping passes would send the same write
  // twice; the server would answer the second from its idempotency table, so
  // nothing would break - and the queue would still show a count that flickers.
  const running = useRef(false);

  useEffect(() => {
    if (!ready) return;
    configureClient({
      offlineStore: ready,
      describeWrite: describe,
      onQueued: () => void ready.all().then(setWrites),
    });
    return () => configureClient({ offlineStore: null, onQueued: () => {} });
  }, [ready]);

  const refresh = useCallback(async () => {
    if (!ready) return;
    setWrites(await ready.all());
  }, [ready]);

  const replay = useCallback(async () => {
    if (!ready || running.current) return;
    running.current = true;
    setSyncing(true);
    try {
      await replayQueue(ready);
    } finally {
      running.current = false;
      setSyncing(false);
      await refresh();
    }
  }, [ready, refresh]);

  const discard = useCallback(
    async (id: string) => {
      if (!ready) return;
      await ready.remove(id);
      await refresh();
    },
    [ready, refresh],
  );

  useEffect(() => {
    void refresh();
    const back = (): void => {
      setOnline(true);
      void replay();
    };
    const gone = (): void => setOnline(false);
    window.addEventListener('online', back);
    window.addEventListener('offline', gone);
    return () => {
      window.removeEventListener('online', back);
      window.removeEventListener('offline', gone);
    };
  }, [refresh, replay]);

  const value = useMemo<QueueState>(
    () => ({
      writes,
      online,
      syncing,
      waiting: writes.filter((w) => w.state === 'waiting').length,
      needsYou: writes.filter((w) => w.state === 'needs-you').length,
      refused: writes.filter((w) => w.state === 'refused').length,
      replay,
      refresh,
      discard,
      store: ready,
    }),
    [writes, online, syncing, replay, refresh, discard, ready],
  );

  if (!ready) return <>{children}</>;
  return <QueueContext.Provider value={value}>{children}</QueueContext.Provider>;
}

/**
 * The queue, or a still one.
 *
 * Returns an empty queue rather than throwing when there is no provider, so a
 * screen rendered on its own in a test is not obliged to know about offline.
 * The indicator reads `configured` to decide whether to draw at all - a status
 * light for a feature that is not installed is worse than no light.
 */
export function useOfflineQueue(): QueueState & { configured: boolean } {
  const value = useContext(QueueContext);
  if (value) return { ...value, configured: true };
  return {
    writes: [],
    online: true,
    syncing: false,
    waiting: 0,
    needsYou: 0,
    refused: 0,
    replay: async () => {},
    refresh: async () => {},
    discard: async () => {},
    store: null,
    configured: false,
  };
}
